import { useReservationList } from '@/features/reservations/hooks/useReservations';
import { allowedActionsForReservation } from '@/features/reservations/hooks/allowedActionsForReservation';
import { useReservationActionHandlers } from '@/features/reservations/hooks/useReservationActionHandlers';
import EmptyState from '@/components/EmptyState';
import ReservationActions from '@/features/reservations/components/ReservationActions';
import ReservationCard from '@/features/reservations/components/ReservationCard';
import CoversByHour, { type HourBar } from '@/features/reservations/components/CoversByHour';
import { useCapacityMode, useShifts } from '@/features/reservation-setup/hooks/useReservationSetup';
import { fromApi, fromDateString, todayString } from '@/utils/datetime';
import {
  type DisplayStatus,
  getDateContext,
  getDisplayStatus,
  getDisplayStatusLabel,
} from '@/utils/status';
import { useT } from '@/i18n';
import type { Reservation, ReservationShift } from '@restaurant/api-client';
import { ReservationStatus } from '@restaurant/types';
import { Block, BlockTitle, List, Preloader, f7 } from 'framework7-react';
import { DateTime } from 'luxon';
import { useRef, useState } from 'react';

// Order the status sections by operational priority: what needs attention
// (pending) first, then the active flow (upcoming → arrived), then the settled
// outcomes. getDisplayStatus only ever returns these keys ('confirmed' is a
// label applied to future 'waiting', never a group key).
const SECTION_ORDER: DisplayStatus[] = [
  'pending',
  'waiting',
  'arrived',
  'seated',
  'in-progress',
  'completed',
  'no-show',
  'rescheduled',
  'cancelled',
  'rejected',
];

/** The covers cap(s) a shift exposes, mode-shaped: SIMPLE has a single per-slot
 *  covers ceiling; ADVANCED has a pacing (per-15-min) cap and/or a concurrent
 *  (simultaneous) cap — both are shown, since they mean different things. */
type CoversCap =
  | { mode: 'SIMPLE'; covers: number }
  | { mode: 'ADVANCED'; pacing: number | null; concurrent: number | null };

/** A reservation shift window resolved for a day: its name, raw "HH:MM" bounds,
 *  whole-hour bounds [openHour, closeHour) (close exclusive, rounded out so
 *  17:00–22:30 covers hours 17…22), and its covers cap(s) (null = no cap). */
type ShiftWindow = {
  label: string;
  start: string;
  end: string;
  openHour: number;
  closeHour: number;
  coversCap: CoversCap | null;
};

/** The shift's covers ceiling(s) from its rules: covers-per-slot in SIMPLE mode,
 *  else BOTH the pacing (per-15-min) and concurrent (simultaneous) caps in
 *  ADVANCED. Null when the shift is uncapped on every relevant axis. */
function shiftCoversCap(rules: ReservationShift['rules']): CoversCap | null {
  if (rules.mode === 'SIMPLE') {
    return rules.maxCoversPerSlot != null
      ? { mode: 'SIMPLE', covers: rules.maxCoversPerSlot }
      : null;
  }
  const pacing = rules.pacingMaxCovers ?? null;
  const concurrent = rules.maxConcurrentCovers ?? null;
  return pacing == null && concurrent == null ? null : { mode: 'ADVANCED', pacing, concurrent };
}

/** Render a covers cap for display: SIMPLE → "max N/slot"; ADVANCED → both the
 *  pacing and concurrent caps, "∞" for an uncapped axis (e.g. "20/15' · 50 …"). */
function formatCoversCap(t: ReturnType<typeof useT>, cap: CoversCap): string {
  if (cap.mode === 'SIMPLE') return t.reservations.slotCap(cap.covers);
  const p = cap.pacing != null ? `${cap.pacing}` : '∞';
  const c = cap.concurrent != null ? `${cap.concurrent}` : '∞';
  return t.reservations.advCap(p, c);
}

/**
 * The reservation WINDOW shifts (the capacity model's Shift entity) that apply
 * to a weekday, so the covers chart names each real shift and runs its axis out
 * to the last shift's end. BASELINE shifts have no time window, so they can't be
 * drawn - only WINDOW shifts scheduled on this day appear. Sorted by start.
 */
function dayShiftWindows(shifts: ReservationShift[] | undefined, dayOfWeek: number): ShiftWindow[] {
  if (!shifts) return [];
  return shifts
    .filter(
      (s): s is Extract<ReservationShift, { scope: 'WINDOW' }> =>
        s.scope === 'WINDOW' && s.daysOfWeek.includes(dayOfWeek),
    )
    .map((s) => {
      const [oh, om] = s.startTime.split(':').map(Number);
      const [ch, cm] = s.endTime.split(':').map(Number);
      return {
        label: s.label,
        start: s.startTime,
        end: s.endTime,
        openHour: Math.floor((oh * 60 + om) / 60),
        closeHour: Math.ceil((ch * 60 + cm) / 60),
        coversCap: shiftCoversCap(s.rules),
      };
    })
    .sort((a, b) => a.openHour - b.openHour);
}

/**
 * Reservation list as a *view* - no navbar, no date picker, no ptr. Renders
 * inside the Reservations workspace shell, which owns date, view-mode and
 * selection. Tapping a card asks the shell to open the inspector instead of
 * navigating away.
 */
export default function ListView({
  date,
  selectedId,
  onSelectReservation,
  statusFilter = 'all',
}: {
  date: string;
  selectedId: string | null;
  onSelectReservation: (reservation: Reservation) => void;
  /** Narrow the day to one display status; 'all' shows every section. */
  statusFilter?: DisplayStatus | 'all';
}) {
  const t = useT();
  const [actionTarget, setActionTarget] = useState<Reservation | null>(null);
  // Framework7's <Popover> ref exposes `.el` but ships no clean instance type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const popoverRef = useRef<any>(null);

  const { data: reservations, isLoading, error } = useReservationList(date);
  const { data: shifts } = useShifts(true);
  const { data: capacityView } = useCapacityMode();
  const {
    handleConfirm,
    handleReactivate,
    handleReject,
    handleCancel,
    handleMarkArrived,
    handleMarkSeated,
    handleMarkInProgress,
    handleMarkCompleted,
    handleMarkNoShow,
    handleMarkNotStarted,
    handleReschedule,
  } = useReservationActionHandlers();
  const allowedActions = allowedActionsForReservation(actionTarget);

  const handleBadgeTap = (reservation: Reservation, targetEl: HTMLElement) => {
    setActionTarget(reservation);
    requestAnimationFrame(() => {
      if (popoverRef.current?.el) f7.popover.open(popoverRef.current.el, targetEl);
    });
  };

  const allItems = reservations?.data ?? [];
  // Apply the status filter to the day's reservations (client-side - the list is
  // already the whole day). 'all' keeps everything.
  const items =
    statusFilter === 'all'
      ? allItems
      : allItems.filter((r) => getDisplayStatus(r.status, r.arrivalStatus) === statusFilter);

  if (isLoading) {
    return (
      <Block style={{ textAlign: 'center', paddingTop: 48 }}>
        <Preloader size={32} />
      </Block>
    );
  }
  if (error) {
    return (
      <EmptyState
        icon="exclamationmark_triangle"
        title={t.reservations.empty.errorTitle}
        description={t.reservations.empty.errorDesc}
      />
    );
  }
  if (items.length === 0) {
    return (
      <EmptyState
        icon="calendar"
        title={t.reservations.empty.noneTitle}
        description={t.reservations.empty.noneDesc}
      />
    );
  }

  // Bucket the day's reservations by display status, then render one section per
  // non-empty status in operational order - so staff scan each status as its own
  // block instead of one undifferentiated list.
  const groups = new Map<DisplayStatus, Reservation[]>();
  for (const r of items) {
    const ds = getDisplayStatus(r.status, r.arrivalStatus);
    const bucket = groups.get(ds);
    if (bucket) bucket.push(r);
    else groups.set(ds, [r]);
  }
  const sections = SECTION_ORDER.map((ds) => ({ ds, rows: groups.get(ds) ?? [] })).filter(
    (s) => s.rows.length > 0,
  );

  // Day-summary: a covers-by-hour pacing histogram (CoversByHour) with a text
  // caption beneath it. Covers (total guests) is the real load number and is
  // shown nowhere else; the chart shows *when* that load lands, and the caption
  // names the peak + next-2h in words (also the chart's accessible fallback).
  // Status counts stay in the section headers, so we never repeat them here.
  const isToday = date === todayString();
  const now = DateTime.now();
  // The current hour; the chart starts here on today (served hours are trimmed).
  // null on any non-today date, where the whole day is upcoming.
  const nowHour = isToday ? now.hour : null;
  // Day-load numbers count only reservations that still represent real covers -
  // CANCELLED and REJECTED bookings freed their seats, so counting them (and
  // their party sizes) would overstate the total, the guest count, and every
  // hourly bucket. The section list below still shows them; only the summary
  // aggregates exclude them.
  const counted = items.filter(
    (r) => r.status !== ReservationStatus.CANCELLED && r.status !== ReservationStatus.REJECTED,
  );
  const guests = counted.reduce((sum, r) => sum + r.partySize, 0);

  // Bucket covers by local hour.
  const guestsByHour = new Map<number, number>();
  for (const r of counted) {
    const hour = fromApi(r.time).hour;
    guestsByHour.set(hour, (guestsByHour.get(hour) ?? 0) + r.partySize);
  }
  const dayOfWeek = fromDateString(date).weekday % 7; // Luxon Mon=1..Sun=7 → 0=Sun
  const shiftWindows = dayShiftWindows(shifts, dayOfWeek);
  // The BASELINE shift's cap - the default limit that applies outside any
  // specific WINDOW shift. Shown as a card footnote (it has no time span).
  // When no baseline is configured yet, fall back to the backend's default
  // baseline rules (DEFAULT_SHIFT_RULES) - the SAME values the booking engine
  // resolves for an unconfigured restaurant - so the footnote reflects the real
  // in-effect cap instead of vanishing.
  const baselineShift = shifts?.find((s) => s.scope === 'BASELINE');
  const baselineRules = baselineShift?.rules ?? capacityView?.defaultBaselineRules ?? null;
  const baselineCap = baselineRules ? shiftCoversCap(baselineRules) : null;

  // Show the full day (00:00–23:00) so the past is never hidden - served hours
  // stay visible (muted), not trimmed away.
  const minHour = 0;
  const maxHour = 23;
  const bars: HourBar[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    guests: guestsByHour.get(hour) ?? 0,
  }));

  // Shift bands: each reservation shift that overlaps the visible axis, named by
  // its label with the total covers booked into it (over the whole shift, not
  // just the visible part). Gives per-shift load the hourly bars don't sum.
  const shiftBands = shiftWindows
    .map((s) => {
      const startHour = Math.max(minHour, s.openHour);
      const endHourExcl = Math.min(maxHour + 1, s.closeHour);
      if (endHourExcl <= startHour) return null; // outside the visible window
      let g = 0;
      for (const r of counted) {
        const time = fromApi(r.time).toFormat('HH:mm');
        if (time >= s.start && time < s.end) g += r.partySize;
      }
      return {
        startHour,
        endHour: endHourExcl,
        name: s.label,
        detail: t.reservations.guestsCount(g),
        // Per-slot limit on its own line so it never crowds the name/covers.
        cap: s.coversCap != null ? formatCoversCap(t, s.coversCap) : null,
      };
    })
    .filter(
      (
        b,
      ): b is {
        startHour: number;
        endHour: number;
        name: string;
        detail: string;
        cap: string | null;
      } => b !== null,
    );

  // Peak among *upcoming* hours (skip the past on today) so the caption names
  // the next rush to prepare for, not one that's already been served.
  let peakHour = -1;
  let peakGuests = 0;
  for (const { hour, guests: g } of bars) {
    if (nowHour != null && hour < nowHour) continue;
    if (g > peakGuests) {
      peakHour = hour;
      peakGuests = g;
    }
  }
  const peakLabel =
    peakHour >= 0
      ? `${String(peakHour).padStart(2, '0')}:00–${String((peakHour + 1) % 24).padStart(2, '0')}:00`
      : '';

  // Next-2h load - today only, and only unfiltered (a status filter would make
  // the histogram a subset of one status, not the day's shape).
  const nowHourFloat = now.hour + now.minute / 60;
  let soonGuests = 0;
  if (isToday && statusFilter === 'all') {
    for (const { hour, guests: g } of bars) {
      if (hour >= now.hour && hour < now.hour + 2) soonGuests += g;
    }
  }
  const showChart = statusFilter === 'all' && bars.length > 1;
  // Hours from the chart's left edge to "now" (the component turns this into a
  // proportional position across the width). null hides the marker.
  const nowOffsetHours =
    showChart && isToday && nowHourFloat >= minHour && nowHourFloat <= maxHour + 1
      ? nowHourFloat - minHour
      : null;

  // Only the next-2h load is shown in words - the peak is already the tallest
  // labelled bar, so a "peak" caption would just restate the chart. Peak still
  // rides along in the chart's aria label for screen readers.
  const subParts = [soonGuests > 0 ? t.reservations.nextWindow(soonGuests) : ''].filter(Boolean);
  const chartAria = `${t.reservations.covers(counted.length, guests)}${
    peakHour >= 0 ? `. ${t.reservations.peak(peakLabel, peakGuests)}` : ''
  }`;

  return (
    <>
      {/* Day-overview panel: framed as a card so it reads as a distinct summary,
          set apart from the reservation list below. */}
      <div
        style={{
          margin: '16px 16px 8px',
          padding: '12px 16px',
          background: 'var(--f7-card-bg-color, #fff)',
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>
          {isToday ? t.reservations.today : date}
          {' • '}
          {t.reservations.covers(counted.length, guests)}
        </div>
        {showChart && (
          <>
            <div
              style={{
                marginTop: 6,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'var(--f7-text-color-secondary, #8e8e93)',
                }}
              >
                {t.reservations.guestsByHour}
              </span>
              {baselineCap != null && (
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--f7-text-color-tertiary, #a1a1a6)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.reservations.defaultShift}
                  {' · '}
                  {formatCoversCap(t, baselineCap)}
                </span>
              )}
            </div>
            <CoversByHour
              bars={bars}
              nowHour={nowHour}
              nowOffsetHours={nowOffsetHours}
              nowLabel={t.reservations.nowLabel}
              shifts={shiftBands}
              ariaLabel={chartAria}
            />
          </>
        )}
        {subParts.length > 0 && (
          <div
            style={{
              marginTop: 6,
              fontSize: 13,
              color: 'var(--f7-text-color-secondary, #8e8e93)',
            }}
          >
            {subParts.join('  ·  ')}
          </div>
        )}
      </div>

      {sections.map(({ ds, rows }) => {
        // All rows in this view share the selected date, so the date context (and
        // thus the 'waiting'→"Confirmed" relabel for future days) is consistent.
        const label = getDisplayStatusLabel(
          ds,
          getDateContext(rows[0].time),
          t.reservations.displayStatus,
        );
        return (
          <div key={ds}>
            <BlockTitle>
              {label}
              {' • '}
              {rows.length}
            </BlockTitle>
            <List inset dividers className="reservation-rows" style={{ marginBottom: 16 }}>
              {/* Invoked as a function (not <ReservationCard/>) so each child is a
                  direct ListItem - see ReservationCard / FRONTEND_PRACTICES Rule 9. */}
              {rows.map((reservation) =>
                ReservationCard({
                  reservation,
                  t,
                  onTap: onSelectReservation,
                  onBadgeTap: handleBadgeTap,
                  onSwipeConfirm: handleConfirm,
                  onSwipeReject: handleReject,
                  isSelected: reservation.id === selectedId,
                }),
              )}
            </List>
          </div>
        );
      })}

      <ReservationActions
        ref={popoverRef}
        reservation={actionTarget}
        allowedActions={allowedActions}
        onClose={() => f7.popover.close()}
        onConfirm={handleConfirm}
        onReactivate={handleReactivate}
        onReject={handleReject}
        onCancel={handleCancel}
        onMarkArrived={handleMarkArrived}
        onMarkSeated={handleMarkSeated}
        onMarkInProgress={handleMarkInProgress}
        onMarkCompleted={handleMarkCompleted}
        onMarkNoShow={handleMarkNoShow}
        onMarkNotStarted={handleMarkNotStarted}
        onReschedule={handleReschedule}
      />
    </>
  );
}
